import React, { useState, useRef, useEffect } from "react";
import { X, ZoomIn, ZoomOut, Play, Pause, Volume2, VolumeX, Mic, Download, Loader2, Maximize2 } from "lucide-react";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import { Button } from "@/components/ui/button";
import { useAuth, getAuthenticatedMediaUrl } from "@/App";

export default function ImageLightbox({ src, imageUrl, alt, onClose, mediaType, hideLogo = false }) {
  const { user } = useAuth();
  const [zoom, setZoom] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [showFullSize, setShowFullSize] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const imageRef = useRef(null);

  // Support both src and imageUrl props - add auth token for admin users
  const rawMediaSrc = src || imageUrl;
  const thumbSrc = getAuthenticatedMediaUrl(rawMediaSrc);
  // Full-size URL is the thumb URL with /thumb?size=NNN stripped out.
  // Keeps any trailing query params (e.g. &token=…).
  const fullSrc = thumbSrc
    ? thumbSrc.replace(/\/thumb\?size=\d+(&|$)/, (m, sep) => (sep === '&' ? '?' : ''))
    : thumbSrc;
  const isThumb = thumbSrc && thumbSrc !== fullSrc;
  const mediaSrc = isThumb && showFullSize ? fullSrc : thumbSrc;

  // Don't render if no media
  if (!mediaSrc) return null;

  // Detect media type (use raw URL without token)
  const srcLower = rawMediaSrc.toLowerCase();
  const isVideo = mediaType === 'video' || 
    srcLower.includes('video') || 
    /\.(mp4|mov|avi|webm|mkv)($|\?)/i.test(srcLower) ||
    mediaSrc.startsWith('data:video');
  const isAudio = mediaType === 'audio' || 
    srcLower.includes('audio') || 
    /\.(mp3|wav|ogg|m4a|aac|webm)($|\?)/i.test(srcLower) ||
    mediaSrc.startsWith('data:audio');
  const isImage = !isVideo && !isAudio;

  // Extract filename from any URL format (R2 URL, API URL, etc.)
  const getFilename = () => {
    // Use raw URL (without token) to extract filename
    const urlPath = rawMediaSrc.split('?')[0]; // Remove query params
    const parts = urlPath.split('/');
    return parts[parts.length - 1];
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Download media directly from server (already watermarked for non-admin users)
  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const filename = getFilename();

      // Use the full authenticated media URL with download flag
      // This ensures we hit the correct backend server (not the frontend)
      const downloadUrl = mediaSrc.includes('?') ? `${mediaSrc}&download=true` : `${mediaSrc}?download=true`;

      // Fetch through backend (applies watermarks for non-admin)
      const response = await fetch(downloadUrl, { credentials: 'include' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Check if we got HTML (error page) instead of media
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        throw new Error('File not found on server');
      }

      const blob = await response.blob();

      // Get extension from content-type or filename
      // Note: Server already applies watermarks for non-admin users
      let ext = 'jpg';
      if (contentType.includes('png')) ext = 'png';
      else if (contentType.includes('gif')) ext = 'gif';
      else if (contentType.includes('webp')) ext = 'webp';
      else if (contentType.includes('mp4') || contentType.includes('video')) ext = 'mp4';
      else if (contentType.includes('mp3') || contentType.includes('audio')) ext = 'mp3';
      else if (filename.includes('.')) ext = filename.split('.').pop();

      const timestamp = Date.now();
      const downloadFilename = `CHSP_${timestamp}.${ext}`;

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // iOS Safari doesn't support download attribute - convert to data URL and open
      if (isIOS) {
        // Convert blob to data URL for iOS (more reliable for saving)
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          // Create a simple HTML page with the image that user can long-press to save
          const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta name="viewport" content="width=device-width, initial-scale=1">
              <title>Save Image</title>
              <style>
                body { margin: 0; padding: 20px; background: #000; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; }
                img { max-width: 100%; height: auto; border-radius: 8px; }
                p { color: #fff; text-align: center; margin-top: 20px; font-family: -apple-system, sans-serif; }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="Download">
              <p>Long-press the image and tap "Save to Photos"</p>
            </body>
            </html>
          `;
          const newTab = window.open('', '_blank');
          if (newTab) {
            newTab.document.write(htmlContent);
            newTab.document.close();
          } else {
            // Fallback: open data URL directly
            window.open(dataUrl, '_blank');
          }
        };
        reader.readAsDataURL(blob);
        URL.revokeObjectURL(blobUrl);
      } else {
        // Standard download for other browsers
        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = blobUrl;
        anchor.download = downloadFilename;

        // Must append to body for mobile browsers
        document.body.appendChild(anchor);

        // Trigger click
        anchor.click();

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(anchor);
          URL.revokeObjectURL(blobUrl);
        }, 100);
      }

      // Log the download
      try {
        await fetch('/api/audit/media-download', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ file_id: filename, filename: filename })
        });
      } catch (logErr) {
        console.error('Failed to log download:', logErr);
      }

    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed: ' + err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const togglePlayPause = () => {
    const mediaEl = videoRef.current || audioRef.current;
    if (mediaEl) {
      if (mediaEl.paused) {
        mediaEl.play();
        setIsPlaying(true);
      } else {
        mediaEl.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    const mediaEl = videoRef.current || audioRef.current;
    if (mediaEl) {
      mediaEl.muted = !mediaEl.muted;
      setIsMuted(mediaEl.muted);
    }
  };

  return (
    // Register the lightbox as a Radix DismissableLayer "branch" so any enclosing
    // Dialog/Popover treats clicks/focus inside it as inside-the-tree and does
    // not dismiss. The default DismissableLayerContext is a singleton across
    // all Radix overlay primitives, so a branch added here is recognized by
    // every parent layer simultaneously.
    <DismissableLayerBranch
      className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Controls - responsive layout for mobile with iOS safe area */}
      <div
        className="absolute right-2 sm:right-4 flex flex-wrap gap-1 sm:gap-2 z-[10000] max-w-[90vw] justify-end"
        style={{
          top: 'max(12px, env(safe-area-inset-top, 12px))',
          paddingRight: 'env(safe-area-inset-right, 0px)'
        }}
      >
        {isImage && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomOut();
              }}
              className="bg-black/50 hover:bg-black/70 border-white/20"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomIn();
              }}
              className="bg-black/50 hover:bg-black/70 border-white/20"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            {isThumb && !showFullSize && (
              <Button
                size="sm"
                variant="outline"
                title="Load full-size original"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageLoading(true);
                  setShowFullSize(true);
                }}
                className="bg-black/50 hover:bg-black/70 border-white/20"
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
        {(isVideo || isAudio) && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                togglePlayPause();
              }}
              className="bg-black/50 hover:bg-black/70 border-white/20"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              className="bg-black/50 hover:bg-black/70 border-white/20"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
          </>
        )}
        {/* Download button - always visible for images, hide for videos if non-admin */}
        {!(isVideo && user?.role !== "admin") && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            disabled={isDownloading}
            className="bg-blue-600 hover:bg-blue-700 border-blue-500 min-w-[44px] min-h-[44px] text-white"
          >
            {isDownloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="bg-black/50 hover:bg-black/70 border-white/20"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Media Content */}
      <div
        className="relative max-w-7xl max-h-[90vh] overflow-auto flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={(e) => {
          // Prevent long-press context menu on iOS
          e.target.style.webkitTouchCallout = 'none';
        }}
        style={{
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none'
        }}
      >
        {isImage && (
          <div
            className="relative select-none"
            onContextMenu={(e) => e.preventDefault()}
            onTouchStart={(e) => {
              // Store touch start time for long-press detection
              e.target.dataset.touchStart = Date.now();
            }}
            onTouchEnd={(e) => {
              // Prevent default if it was a long press
              const touchStart = parseInt(e.target.dataset.touchStart || 0);
              if (Date.now() - touchStart > 500) {
                e.preventDefault();
              }
            }}
            style={{
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none'
            }}
          >
            <img
              ref={imageRef}
              src={mediaSrc}
              alt={alt}
              draggable="false"
              decoding="async"
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{
                transform: `scale(${zoom})`,
                transition: 'transform 0.2s',
                width: '90vw',
                height: '85vh',
                objectFit: 'contain',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                pointerEvents: 'none',
                opacity: imageLoading ? 0 : 1
              }}
              className="rounded-lg"
            />
            {imageLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-label="Loading"
              >
                <Loader2 className="w-10 h-10 text-white/80 animate-spin" />
              </div>
            )}
            {/* Server-side watermarking now handles all watermarks for non-admin users */}
          </div>
        )}

        {isVideo && (
          <div className="relative">
            <video
              ref={videoRef}
              src={mediaSrc}
              controls={!showPlayButton}
              controlsList={user?.role !== "admin" ? "nodownload" : undefined}
              onContextMenu={(e) => user?.role !== "admin" && e.preventDefault()}
              playsInline
              webkit-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="true"
              preload="metadata"
              muted={false}
              className="max-w-full max-h-[80vh] rounded-lg bg-black"
              style={{ minHeight: '200px', minWidth: '300px' }}
              onPlay={() => {
                setIsPlaying(true);
                setShowPlayButton(false);
              }}
              onPause={() => setIsPlaying(false)}
              onCanPlay={() => {
                // Video is ready to play
              }}
            />
            {/* iOS Play Button Overlay - tap to start video */}
            {showPlayButton && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/50 cursor-pointer rounded-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current) {
                    videoRef.current.play().then(() => {
                      setShowPlayButton(false);
                      setIsPlaying(true);
                    }).catch((err) => {
                      console.log('Play failed:', err);
                      // Try muted play as fallback
                      videoRef.current.muted = true;
                      videoRef.current.play().then(() => {
                        setShowPlayButton(false);
                        setIsPlaying(true);
                        videoRef.current.muted = false;
                      }).catch(() => {});
                    });
                  }
                }}
              >
                <div className="bg-white/20 hover:bg-white/30 rounded-full p-6 transition-colors">
                  <Play className="w-16 h-16 text-white fill-white" />
                </div>
                <p className="absolute bottom-8 text-white text-lg font-medium">Tap to play</p>
              </div>
            )}
            {/* Server-side watermarking now handles all watermarks for non-admin users */}
          </div>
        )}

        {isAudio && (
          <div className="bg-slate-900 p-8 rounded-lg flex flex-col items-center gap-4">
            <Mic className="w-16 h-16 text-blue-500" />
            <p className="text-white text-lg">{alt || 'Audio Recording'}</p>
            <audio
              ref={audioRef}
              src={mediaSrc}
              controls
              autoPlay
              className="w-80"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>
        )}
      </div>

      {/* Hint */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/60 text-sm">
        Click outside to close
      </div>
    </DismissableLayerBranch>
  );
}
